import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Checkbox, Flex, FormControl, FormErrorMessage, FormLabel,
    Grid, GridItem, HStack, Icon, Input, InputGroup, InputLeftElement, InputRightElement,
    IconButton, Portal, Spinner, Text, Tooltip, VStack,
} from '@chakra-ui/react';
import {
    FaArrowsAlt, FaCheck, FaCompress, FaCrosshairs, FaExpand, FaMapMarkerAlt, FaSearch, FaTimes, FaUndo,
} from 'react-icons/fa';
import { criarMotor } from './motorMapa';
import { buscarEnderecoLalamove, enderecoPorPontoLalamove } from '../../services/api';
import {
    aplicarGeocode, campoComErro, divergencia, doCadastro, formatarCep, linhaLalamove,
    numeroConfere, observacaoDoMotorista, PRECISAO, ROTULO_CAMPO, soDigitos, temPonto, trocaDeEndereco,
} from './endereco';

/**
 * Entrada e confirmacao do endereco de entrega da corrida.
 *
 * A versao anterior tratava o endereco como uma string so: a busca devolvia um
 * rotulo e esse rotulo virava o endereco. Como o geocoder so escreve o numero
 * quando conhece o imovel, o numero da casa sumia sozinho — e sumia de novo a
 * cada vez que o operador arrastava o pino, que e justamente o que a tela pedia
 * para ele fazer. Em 20/09/2026, 7 das 11 corridas registradas tinham ido para
 * a Lalamove sem o numero da casa.
 *
 * Aqui a busca e o mapa respondem uma pergunta so: onde fica o ponto. O
 * endereco em si mora em campos separados, o numero vem do cadastro da venda e
 * nada que o geocoder devolva apaga o que ja esta preenchido.
 */

const CENTRO_LOJA = [-23.5225709, -46.689078];

const SeletorEnderecoMapa = ({
    aberto = true,
    destinoInicial = null,
    cadastro = null,
    onChange,
    alturaMapa = 240,
    compacto = false,
}) => {
    const [destino, setDestino] = useState(() => destinoInicial || doCadastro(cadastro || {}));
    const [termo, setTermo] = useState('');
    const [sugestoes, setSugestoes] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [mostrarLista, setMostrarLista] = useState(false);
    const [destacado, setDestacado] = useState(-1);
    const [resolvendoPino, setResolvendoPino] = useState(false);
    const [falhaGeocode, setFalhaGeocode] = useState(null);
    const [divergente, setDivergente] = useState(null);
    const [pontoAnterior, setPontoAnterior] = useState(null);
    const [buscouSemResultado, setBuscouSemResultado] = useState(false);
    const [mapaSolto, setMapaSolto] = useState(false);
    const [mapaCheio, setMapaCheio] = useState(false);
    const mapaCheioRef = useRef(false);

    const mapaRef = useRef(null);
    const zoomPendenteRef = useRef(17);
    const caixaInline = useRef(null);
    const caixaCheia = useRef(null);

    /**
     * O no do mapa e criado fora do React e movido entre os dois containers.
     *
     * Em tela cheia o mapa precisa escapar do modal, e `position: fixed` nao
     * resolve: o ModalContent do Chakra fica com `transform: scale(1)` parado,
     * e isso faz o fixed se ancorar no modal em vez da janela. Entao o container
     * cheio mora num Portal, fora do modal, e este no e transplantado para la —
     * mover um no no DOM preserva o elemento, e o Leaflet continua o mesmo, com
     * o mesmo pino e os mesmos handlers. Recriar o mapa perderia tudo isso.
     */
    const noDoMapa = useRef(null);
    if (noDoMapa.current === null && typeof document !== 'undefined') {
        noDoMapa.current = document.createElement('div');
        noDoMapa.current.style.width = '100%';
        noDoMapa.current.style.height = '100%';
    }
    const buscaRef = useRef(null);
    const pedidoRef = useRef(0);
    const buscaAtualRef = useRef(0);
    const destinoRef = useRef(destino);
    const moverPinoRef = useRef(() => {});
    const onChangeRef = useRef(onChange);

    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
    useEffect(() => { mapaCheioRef.current = mapaCheio; }, [mapaCheio]);

    const tamanho = compacto ? 'md' : 'sm';
    const botaoMapa = compacto ? 'sm' : 'xs';
    const original = useMemo(() => doCadastro(cadastro || {}), [cadastro]);
    const erros = useMemo(() => campoComErro(destino, cadastro), [destino, cadastro]);
    const linha = useMemo(() => linhaLalamove(destino), [destino]);
    const info = PRECISAO[destino.precisao] || null;

    const numeroDivergente = useMemo(
        () => Boolean(cadastro?.numero) && !destino.semNumero && Boolean(destino.numero)
            && !numeroConfere(destino.numero, cadastro.numero),
        [destino.numero, destino.semNumero, cadastro],
    );

    const troca = useMemo(() => trocaDeEndereco(destino, cadastro), [destino, cadastro]);

    /**
     * Toda mudanca passa por aqui: o estado e o que o pai recebe nao se separam.
     *
     * O novo valor e calculado a partir do ref, fora do updater do setState. Na
     * primeira versao o onChange do pai era chamado de dentro do updater, o que
     * e atualizar um componente durante a renderizacao de outro: o React
     * descartava a atualizacao e o pai ficava com o valor inicial para sempre.
     * Na pratica, tudo que o operador corrigia no formulario nao chegava ao
     * payload — o mesmo tipo de perda silenciosa que este componente existe
     * para evitar.
     */
    const alterar = useCallback((mudanca) => {
        const atual = destinoRef.current;
        const novo = typeof mudanca === 'function' ? mudanca(atual) : { ...atual, ...mudanca };

        destinoRef.current = novo;
        setDestino(novo);
        if (onChangeRef.current) onChangeRef.current(novo);
    }, []);

    // ------------------------------------------------------------------ mapa
    useEffect(() => {
        if (!noDoMapa.current || mapaRef.current) return undefined;

        // O mapa vive dentro de um modal que rola. Com o arrasto ligado, o dedo
        // no celular movia o mapa em vez de rolar o formulario, e a roda do
        // mouse no desktop dava zoom em vez de descer a pagina. Os dois ficam
        // desligados e o operador liga o arrasto quando quiser, no botao.
        let vivo = true;

        criarMotor(noDoMapa.current, {
            centro: CENTRO_LOJA,
            zoom: 12,
            aoClicar: (lat, lng) => moverPinoRef.current(lat, lng),
        }, (motor) => {
            if (!vivo) {
                motor.destruir();
                return;
            }
            mapaRef.current = motor;
            const d = destinoRef.current;
            if (temPonto(d)) posicionarRef.current(d.lat, d.lng, zoomPendenteRef.current);
        });

        return () => {
            vivo = false;
            if (mapaRef.current) mapaRef.current.destruir();
            mapaRef.current = null;
        };
    }, []);

    // o mapa nasce cinza quando e criado dentro de um modal que ainda esta animando
    useEffect(() => {
        if (!aberto || !mapaRef.current) return undefined;
        const t = setTimeout(() => mapaRef.current && mapaRef.current.redimensionar(), 250);
        const obs = new ResizeObserver(() => mapaRef.current && mapaRef.current.redimensionar());
        if (noDoMapa.current) obs.observe(noDoMapa.current);

        return () => { clearTimeout(t); obs.disconnect(); };
    }, [aberto]);

    /**
     * Leva o no do mapa para um container e reencaixa o Leaflet.
     *
     * Chamado por ref de callback, nao por efeito: o Portal do Chakra devolve
     * null no primeiro render e so cria o container dentro de um efeito dele.
     * Um efeito nosso rodava antes disso, nao achava a caixa da tela cheia,
     * desistia e nunca mais tentava — e a tela cheia abria com um retangulo
     * cinza no lugar do mapa.
     */
    const adotarMapa = useCallback((destino) => {
        const no = noDoMapa.current;
        if (!destino || !no || no.parentNode === destino) return;

        destino.appendChild(no);

        // o Leaflet precisa remedir depois que o navegador posicionou a caixa
        requestAnimationFrame(() => {
            if (!mapaRef.current) return;
            mapaRef.current.redimensionar();
            const d = destinoRef.current;
            if (temPonto(d)) mapaRef.current.centralizar(d.lat, d.lng, mapaRef.current.zoom(), false);
        });
    }, []);

    /** A caixa inline nunca desmonta, entao o ref dela nao dispara na volta. */
    useEffect(() => {
        if (!mapaCheio) adotarMapa(caixaInline.current);
    }, [mapaCheio, adotarMapa]);

    const refCaixaInline = useCallback((el) => {
        caixaInline.current = el;
        if (el && !mapaCheioRef.current) adotarMapa(el);
    }, [adotarMapa]);

    const refCaixaCheia = useCallback((el) => {
        caixaCheia.current = el;
        if (el) adotarMapa(el);
    }, [adotarMapa]);

    // Em tela cheia o dedo arrasta o mapa; ao fechar, volta a rolar a pagina.
    // So age quando esta cheio: o mapa ja nasce com o arrasto desligado, e
    // mexer nisso na montagem seria trabalho a toa.
    useEffect(() => {
        const mapa = mapaRef.current;
        if (!mapa || !mapaCheio) return undefined;

        mapa.arrasto(true);

        // o botao "mover mapa" nao existe em tela cheia, entao o estado dele
        // no momento de abrir e o que vale na volta
        const soltoAntes = mapaSolto;

        return () => {
            if (!mapaRef.current || soltoAntes) return;
            mapaRef.current.arrasto(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapaCheio]);

    // Esc no dialogo de divergencia mantem o que o operador escreveu
    useEffect(() => {
        if (!divergente) return undefined;

        const aoTeclar = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            alterar({ divergenciaAssumida: true });
            setDivergente(null);
        };

        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [divergente, alterar]);

    /**
     * Mede a janela que o usuario realmente ve.
     *
     * `position: fixed; inset: 0` se ancora no layout viewport, que no celular
     * pode ser mais largo que a tela quando a pagina atras tem transbordo
     * horizontal ou o usuario deu pinch. Foi o que aconteceu: a barra de cima e
     * os botoes do mapa ficaram para fora da direita, e nao havia como fechar a
     * tela cheia.
     *
     * visualViewport da a area visivel e o deslocamento dela. Sem a API (ou
     * antes do primeiro calculo), cai em 100vw/100dvh, que e o que havia antes.
     */
    useEffect(() => {
        if (!mapaCheio && !divergente) return undefined;

        const vv = window.visualViewport;
        const raiz = document.documentElement;

        const medir = () => {
            const largura = vv ? vv.width : window.innerWidth;
            const altura = vv ? vv.height : window.innerHeight;
            raiz.style.setProperty('--taga-mapa-w', `${Math.round(largura)}px`);
            raiz.style.setProperty('--taga-mapa-h', `${Math.round(altura)}px`);
            raiz.style.setProperty('--taga-mapa-x', `${Math.round(vv ? vv.offsetLeft : 0)}px`);
            raiz.style.setProperty('--taga-mapa-y', `${Math.round(vv ? vv.offsetTop : 0)}px`);
            if (mapaRef.current) mapaRef.current.redimensionar();
        };

        medir();
        vv?.addEventListener('resize', medir);
        vv?.addEventListener('scroll', medir);
        window.addEventListener('orientationchange', medir);

        return () => {
            vv?.removeEventListener('resize', medir);
            vv?.removeEventListener('scroll', medir);
            window.removeEventListener('orientationchange', medir);
            ['--taga-mapa-w', '--taga-mapa-h', '--taga-mapa-x', '--taga-mapa-y']
                .forEach((v) => raiz.style.removeProperty(v));
        };
    }, [mapaCheio, divergente]);

    // Esc fecha, e a pagina atras nao rola enquanto o mapa esta cheio
    useEffect(() => {
        if (!mapaCheio) return undefined;

        const aoTeclar = (e) => { if (e.key === 'Escape') setMapaCheio(false); };
        const overflowAntes = document.body.style.overflow;

        document.addEventListener('keydown', aoTeclar);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', aoTeclar);
            document.body.style.overflow = overflowAntes;
        };
    }, [mapaCheio]);

    const posicionar = useCallback((lat, lng, zoom = 17) => {
        const mapa = mapaRef.current;
        if (lat === null || lng === null) return;
        if (!mapa) {
            zoomPendenteRef.current = zoom;
            return;
        }

        mapa.pino(lat, lng, (la, ln) => moverPinoRef.current(la, ln));
        mapa.centralizar(lat, lng, Math.max(mapa.zoom(), zoom), true);
    }, []);

    const posicionarRef = useRef(posicionar);
    useEffect(() => { posicionarRef.current = posicionar; }, [posicionar]);

    /**
     * Mover o pino troca o ponto e so o ponto.
     *
     * O texto do endereco nao e reescrito com o que a geocodificacao reversa
     * devolve: era exatamente ali que o numero da casa se perdia, porque o
     * reverso quase nunca traz house_number. Se a rua do ponto for outra, a
     * tela pergunta em vez de decidir sozinha.
     */
    const moverPino = useCallback(async (lat, lng) => {
        // Guarda o estado inteiro, nao so a coordenada: mover o pino tambem
        // pode completar campos vazios, e desfazer tem de desfazer tudo isso.
        setPontoAnterior(destinoRef.current);

        posicionar(lat, lng, 17);
        alterar({ lat, lng, precisao: 'manual', pontoConfirmado: true, divergenciaAssumida: false });
        setDivergente(null);

        setResolvendoPino(true);
        const meu = ++pedidoRef.current;
        try {
            const res = await enderecoPorPontoLalamove(lat, lng);
            if (meu !== pedidoRef.current) return;

            const componentes = res?.endereco?.componentes;
            if (componentes) {
                const conflito = divergencia(destinoRef.current, componentes);
                if (conflito) setDivergente(conflito);
                else alterar((atual) => aplicarGeocode(atual, componentes, {}, 'completar'));
            }
            setFalhaGeocode(null);
        } catch (e) {
            // Sem rede ou Nominatim fora: o ponto vale do mesmo jeito, so nao
            // da para conferir o texto contra ele.
            setFalhaGeocode('Não consegui conferir o endereço deste ponto. O pino vale, confira os campos à mão.');
        } finally {
            if (meu === pedidoRef.current) setResolvendoPino(false);
        }
    }, [alterar, posicionar]);

    useEffect(() => { moverPinoRef.current = moverPino; }, [moverPino]);

    // o modal desmonta o seletor ao trocar de etapa: o debounce nao pode
    // disparar uma busca depois disso
    useEffect(() => () => { if (buscaRef.current) clearTimeout(buscaRef.current); }, []);

    /**
     * Mexer no endereco derruba a confirmacao do pino.
     *
     * O pino foi resolvido para o texto anterior. Se o operador troca "Rua A"
     * por "Rua B", ou 1203 por 1205, a coordenada continua onde estava — e a
     * corrida sairia com o texto de um lugar e o ponto de outro, que e a forma
     * mais silenciosa de entregar no endereco errado. Entao qualquer mudanca no
     * que define o local rebaixa a precisao e pede conferencia de novo.
     *
     * Complemento e referencia ficam de fora: eles nao movem a porta.
     */
    const mudouDeLugar = (campo, valor) => {
        const anterior = destinoRef.current[campo] ?? '';

        if (campo === 'numero' || campo === 'cep') {
            return soDigitos(valor) !== soDigitos(anterior);
        }

        return String(valor).trim().toLowerCase() !== String(anterior).trim().toLowerCase();
    };

    const alterarLocal = (campo, valor) => {
        const invalida = mudouDeLugar(campo, valor) && temPonto(destinoRef.current);

        alterar({
            [campo]: valor,
            ...(campo === 'logradouro' ? { divergenciaAssumida: false } : {}),
            ...(invalida
                ? {
                    precisao: destinoRef.current.precisao === 'exata' ? 'rua' : destinoRef.current.precisao,
                    pontoConfirmado: false,
                }
                : {}),
        });
    };

    const alternarArrasto = () => {
        const mapa = mapaRef.current;
        if (!mapa) return;

        const soltar = !mapaSolto;
        mapa.arrasto(soltar);
        setMapaSolto(soltar);
    };

    const desfazerPino = () => {
        if (!pontoAnterior) return;
        if (temPonto(pontoAnterior)) posicionar(pontoAnterior.lat, pontoAnterior.lng, 17);
        alterar(() => pontoAnterior);
        setPontoAnterior(null);
        setDivergente(null);
    };

    // ----------------------------------------------------------------- busca
    const buscar = useCallback(async (q) => {
        if (!q || q.trim().length < 4) {
            setSugestoes([]);
            return;
        }
        setBuscando(true);
        setBuscouSemResultado(false);

        // Digitar rapido dispara buscas em sequencia, e elas nao voltam em
        // ordem: sem este contador, a resposta de um termo velho sobrescreve a
        // do termo atual e o operador escolhe sugestao de outro endereco.
        const meu = ++buscaAtualRef.current;
        try {
            const res = await buscarEnderecoLalamove(q.trim());
            if (meu !== buscaAtualRef.current) return;

            const lista = res?.resultados || [];
            setSugestoes(lista);
            setMostrarLista(lista.length > 0);
            setBuscouSemResultado(lista.length === 0);
            setDestacado(-1);
            setFalhaGeocode(null);
        } catch (e) {
            if (meu !== buscaAtualRef.current) return;
            setSugestoes([]);
            setMostrarLista(false);
            setFalhaGeocode('A busca de endereços não respondeu. Preencha os campos à mão e marque o ponto no mapa.');
        } finally {
            if (meu === buscaAtualRef.current) setBuscando(false);
        }
    }, []);

    const aoDigitar = (v) => {
        setTermo(v);
        if (buscaRef.current) clearTimeout(buscaRef.current);
        buscaRef.current = setTimeout(() => buscar(v), 500);
    };

    /** A sugestao escolhida define o ponto e completa o que falta — nunca apaga. */
    const escolher = (s) => {
        setMostrarLista(false);
        setSugestoes([]);
        setTermo('');
        setPontoAnterior(null);
        setDivergente(null);
        setFalhaGeocode(null);
        setBuscouSemResultado(false);

        alterar((atual) => aplicarGeocode(atual, s.componentes || {}, {
            lat: s.lat,
            lng: s.lng,
            precisao: s.precisao || 'rua',
            pontoConfirmado: s.precisao === 'exata',
            enderecoConfirmado: false,
        }));

        posicionar(s.lat, s.lng, s.precisao === 'exata' ? 18 : 16);
    };

    const aoTeclar = (e) => {
        if (!mostrarLista || sugestoes.length === 0) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setDestacado((i) => Math.min(i + 1, sugestoes.length - 1)); }
        if (e.key === 'ArrowUp') { e.preventDefault(); setDestacado((i) => Math.max(i - 1, 0)); }
        if (e.key === 'Enter' && destacado >= 0) { e.preventDefault(); escolher(sugestoes[destacado]); }
        if (e.key === 'Escape') { setMostrarLista(false); }
    };

    /** Procura no mapa o endereco que esta escrito nos campos. */
    const localizarEscrito = () => {
        const alvo = linhaLalamove(destinoRef.current);
        if (alvo.length < 8) return;
        setTermo(alvo);
        buscar(alvo);
    };

    // Primeira carga: posiciona o pino que veio pronto e, se nao veio, procura
    // o endereco da venda sozinho — o operador nao precisa digitar o que o
    // sistema ja sabe.
    const jaIniciou = useRef(false);
    useEffect(() => {
        if (jaIniciou.current || !aberto) return;
        jaIniciou.current = true;

        // Parte do ref, nao da prop: o efeito roda depois da pintura e, se o
        // operador ja tiver digitado nesse intervalo, semear de novo com a prop
        // apaga o que ele escreveu. O useState ja plantou o valor certo.
        const inicial = destinoRef.current;
        if (onChangeRef.current) onChangeRef.current(inicial);

        if (temPonto(inicial)) {
            posicionar(inicial.lat, inicial.lng, 17);
            return;
        }

        const alvo = linhaLalamove(inicial);
        if (alvo.length < 8) return;

        (async () => {
            setBuscando(true);
            try {
                const res = await buscarEnderecoLalamove(alvo);
                const lista = res?.resultados || [];
                if (lista.length === 0) {
                    setBuscouSemResultado(true);
                    return;
                }

                // 'completar': esta busca existe para achar o ponto do endereco
                // que ja esta na venda, nao para reescrever esse endereco.
                const melhor = lista[0];

                // Ninguem escolheu este resultado: ele so vale como confirmado
                // se for um numero exato E na cidade da venda. A busca livre do
                // Nominatim nao confere cidade, entao um "exata" pode ser de
                // outro municipio — e ai o pino ficaria aprovado sozinho.
                const mesmaCidade = !melhor.componentes?.cidade || !inicial.cidade
                    || melhor.componentes.cidade.trim().toLowerCase() === inicial.cidade.trim().toLowerCase();

                alterar((atual) => aplicarGeocode(atual, melhor.componentes || {}, {
                    lat: melhor.lat,
                    lng: melhor.lng,
                    precisao: melhor.precisao || 'rua',
                    pontoConfirmado: melhor.precisao === 'exata' && mesmaCidade,
                }, 'completar'));
                posicionar(melhor.lat, melhor.lng, melhor.precisao === 'exata' ? 18 : 16);
                if (lista.length > 1) setSugestoes(lista);
            } catch (e) {
                setFalhaGeocode('Não consegui localizar o endereço da venda no mapa. Marque o ponto à mão.');
            } finally {
                setBuscando(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    const restaurarDaVenda = () => {
        setDivergente(null);
        setPontoAnterior(null);
        alterar((atual) => ({
            ...original,
            lat: atual.lat,
            lng: atual.lng,
            precisao: atual.precisao === 'exata' ? 'rua' : atual.precisao,
            pontoConfirmado: false,
        }));
    };

    const campo = (id, rotulo, valor, aoMudar, extras = {}) => (
        <FormControl isInvalid={Boolean(erros[extras.chave || id])} isRequired={extras.obrigatorio}>
            <FormLabel htmlFor={id} fontSize="xs" color="gray.600" mb={1}>{rotulo}</FormLabel>
            <Input
                id={id}
                size={tamanho}
                bg="white"
                borderRadius="md"
                value={valor || ''}
                onChange={(e) => aoMudar(e.target.value)}
                placeholder={extras.placeholder}
                inputMode={extras.inputMode}
                maxLength={extras.maxLength}
                autoComplete={extras.autoComplete || 'off'}
                textTransform={extras.maiuscula ? 'uppercase' : undefined}
            />
            {erros[extras.chave || id] && <FormErrorMessage fontSize="xs">{erros[extras.chave || id]}</FormErrorMessage>}
        </FormControl>
    );

    /**
     * Grade do endereco no padrao dos checkouts brasileiros: cada campo com a
     * largura do que ele guarda. Rua e cidade sao longos, numero e UF sao
     * curtos, e antes todos dividiam o espaco igualmente — "Ribeirao Preto"
     * nao cabia enquanto o UF sobrava.
     *
     * Duas colunas em qualquer largura. Quatro quebravam no desktop: ali o
     * formulario divide a linha com o mapa e sobra perto de 320px, o que dava
     * 80px por coluna e cortava o CEP.
     */
    const formulario = (
        <Grid templateColumns="repeat(2, 1fr)" gap={3} alignItems="start">
            <GridItem colSpan={2}>
                {campo('logradouro', 'Rua / avenida', destino.logradouro, (v) => alterarLocal('logradouro', v), {
                    obrigatorio: true, placeholder: 'Rua Espírito Santo', autoComplete: 'address-line1',
                })}
            </GridItem>

            <GridItem colSpan={1}>
                <FormControl isInvalid={Boolean(erros.numero)} isRequired={!destino.semNumero}>
                    <FormLabel htmlFor="numero" fontSize="xs" color="gray.600" mb={1}>Número</FormLabel>
                    <Input
                        id="numero"
                        size={tamanho}
                        bg={destino.semNumero ? 'gray.100' : 'white'}
                        borderRadius="md"
                        fontWeight="bold"
                        // marcado "sem numero", mostra S/N em vez de ficar
                        // vazio com o placeholder de exemplo
                        value={destino.semNumero ? 'S/N' : (destino.numero || '')}
                        isDisabled={destino.semNumero}
                        onChange={(e) => alterarLocal('numero', e.target.value)}
                        placeholder="1203"
                        // teclado de texto, nao numerico: numero de casa no
                        // Brasil tem letra ("1203 A"), quadra e lote e km. A
                        // Lalamove nao tem campo de numero, stops[].address e
                        // texto livre, entao a unica regra e a nossa.
                        inputMode="text"
                        maxLength={20}
                        autoComplete="off"
                    />
                    {erros.numero && <FormErrorMessage fontSize="xs">{erros.numero}</FormErrorMessage>}
                </FormControl>
            </GridItem>

            <GridItem colSpan={2}>
                {campo('complemento', 'Complemento', destino.complemento, (v) => alterar({ complemento: v }), {
                    placeholder: 'Apto 42, bloco B', maxLength: 120,
                })}
            </GridItem>

            <GridItem colSpan={2}>
                {campo('bairro', 'Bairro', destino.bairro, (v) => alterarLocal('bairro', v), {
                    placeholder: 'Vila Leopoldina',
                })}
            </GridItem>

            <GridItem colSpan={2}>
                {campo('cidade', 'Cidade', destino.cidade, (v) => alterarLocal('cidade', v), { obrigatorio: true })}
            </GridItem>

            <GridItem colSpan={1}>
                {campo('uf', 'UF', destino.uf, (v) => alterarLocal('uf', v.toUpperCase().slice(0, 2)), {
                    obrigatorio: true, maxLength: 2, maiuscula: true,
                })}
            </GridItem>

            <GridItem colSpan={1}>
                {campo('cep', 'CEP', formatarCep(destino.cep), (v) => alterarLocal('cep', soDigitos(v)), {
                    inputMode: 'numeric', maxLength: 9, placeholder: '00000-000',
                })}
            </GridItem>

            <GridItem colSpan={2}>
                <Checkbox
                    size="sm"
                    colorScheme="purple"
                    isChecked={destino.semNumero}
                    onChange={(e) => alterar({
                        semNumero: e.target.checked,
                        numero: e.target.checked ? '' : destino.numero,
                        ...(temPonto(destino) ? { pontoConfirmado: false } : {}),
                    })}
                >
                    <Text fontSize="xs" color="gray.600">Este local não tem número (s/n)</Text>
                </Checkbox>
            </GridItem>

            {numeroDivergente && (
                <GridItem colSpan={2}>
                    <Alert status="warning" borderRadius="md" fontSize="xs" alignItems="start">
                        <AlertIcon boxSize={3} mt="2px" />
                        <Box>
                            <Text>
                                A venda está no número <b>{cadastro.numero}</b> e você digitou <b>{destino.numero}</b>.
                            </Text>
                            <Checkbox
                                mt={1}
                                size="sm"
                                colorScheme="orange"
                                isChecked={Boolean(destino.numeroConfirmado)}
                                onChange={(e) => alterar({ numeroConfirmado: e.target.checked })}
                            >
                                <Text fontSize="xs">Conferi, é {destino.numero} mesmo</Text>
                            </Checkbox>
                        </Box>
                    </Alert>
                </GridItem>
            )}

            {(troca.rua || troca.cidade) && (
                <GridItem colSpan={2}>
                    <Alert status="warning" borderRadius="md" fontSize="xs" alignItems="start">
                        <AlertIcon boxSize={3} mt="2px" />
                        <Box>
                            <Text>
                                A venda é na <b>{troca.ruaCadastrada}, {troca.cidadeCadastrada}</b>
                                {troca.cepCadastrado ? <>, CEP <b>{formatarCep(troca.cepCadastrado)}</b></> : null}.
                                Este endereço é outro.
                            </Text>
                            <Checkbox
                                mt={1}
                                size="sm"
                                colorScheme="orange"
                                isChecked={Boolean(destino.enderecoConfirmado)}
                                onChange={(e) => alterar({ enderecoConfirmado: e.target.checked })}
                            >
                                <Text fontSize="xs">O cliente pediu entrega neste outro endereço</Text>
                            </Checkbox>
                            <Button display="block" mt={1} size="xs" variant="link" colorScheme="purple" onClick={restaurarDaVenda}>
                                Voltar para o endereço da venda
                            </Button>
                        </Box>
                    </Alert>
                </GridItem>
            )}

            <GridItem colSpan={2}>
                {campo('referencia', 'Ponto de referência (opcional)', destino.referencia, (v) => alterar({ referencia: v }), {
                    placeholder: 'Ao lado do posto, portão azul', maxLength: 200,
                })}
            </GridItem>
        </Grid>
    );

    const mapa = (
        <VStack align="stretch" spacing={2}>
            <Box position="relative" borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor={erros.ponto ? 'red.300' : 'gray.200'}>
                <Box ref={refCaixaInline} h={`${alturaMapa}px`} w="100%" bg="gray.100" />

                {!temPonto(destino) && (
                    <Flex position="absolute" inset={0} align="center" justify="center" bg="blackAlpha.500" zIndex={1000} pointerEvents="none" px={4}>
                        <Text fontSize="xs" color="white" fontWeight="bold" textAlign="center">
                            Busque o endereço ou toque no mapa
                        </Text>
                    </Flex>
                )}

                <HStack position="absolute" top={2} right={2} zIndex={1000} spacing={1}>
                    <Tooltip label={mapaSolto ? 'Travar o mapa' : 'Liberar o mapa para arrastar'} hasArrow>
                        <IconButton
                            size={botaoMapa}
                            aria-label={mapaSolto ? 'Travar o mapa' : 'Liberar o mapa para arrastar'}
                            icon={<Icon as={FaArrowsAlt} boxSize={3} />}
                            onClick={alternarArrasto}
                            bg={mapaSolto ? 'purple.500' : 'white'}
                            color={mapaSolto ? 'white' : 'gray.700'}
                            boxShadow="md"
                            _hover={{ bg: mapaSolto ? 'purple.600' : 'gray.50' }}
                        />
                    </Tooltip>
                    {pontoAnterior && (
                        <Tooltip label="Desfazer" hasArrow>
                            <IconButton
                                size={botaoMapa}
                                aria-label="Desfazer"
                                icon={<Icon as={FaUndo} boxSize={3} />}
                                onClick={desfazerPino}
                                bg="white"
                                boxShadow="md"
                                _hover={{ bg: 'gray.50' }}
                            />
                        </Tooltip>
                    )}
                    {temPonto(destino) && (
                        <Tooltip label="Centralizar no pino" hasArrow>
                            <IconButton
                                size={botaoMapa}
                                aria-label="Centralizar no pino"
                                icon={<Icon as={FaCrosshairs} boxSize={3} />}
                                onClick={() => posicionar(destino.lat, destino.lng, 18)}
                                bg="white"
                                boxShadow="md"
                                _hover={{ bg: 'gray.50' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip label="Tela cheia" hasArrow>
                        <IconButton
                            size={botaoMapa}
                            aria-label="ampliar"
                            icon={<Icon as={FaExpand} boxSize={3} />}
                            onClick={() => setMapaCheio(true)}
                            bg="white"
                            boxShadow="md"
                            _hover={{ bg: 'gray.50' }}
                        />
                    </Tooltip>
                </HStack>
            </Box>

            <Flex gap={2} align="center" wrap="wrap" justify="space-between" aria-live="polite">
                <HStack spacing={2} flex={1} minW={0}>
                    {resolvendoPino && <Spinner size="xs" color="purple.500" aria-label="conferindo o ponto" />}
                    {info && <Badge colorScheme={info.cor} variant="subtle" fontSize="10px">{info.rotulo}</Badge>}
                </HStack>
                {temPonto(destino) && (
                    <Text fontSize="10px" color="gray.400">
                        {Number(destino.lat).toFixed(6)}, {Number(destino.lng).toFixed(6)}
                    </Text>
                )}
            </Flex>

            <Text fontSize="10px" color="gray.500">
                Toque no mapa ou arraste o pino até a porta.
            </Text>

            {temPonto(destino) && destino.precisao !== 'exata' && !destino.pontoConfirmado && (
                <Alert status="warning" borderRadius="md" fontSize="xs" alignItems="start">
                    <AlertIcon boxSize={3} mt="2px" />
                    <Box flex={1}>
                        <Text>
                            {destino.precisao === 'rua'
                                ? `O mapa achou a rua, mas não o número ${destino.numero || ''}. O pino caiu no meio dela.`
                                : 'Este pino não foi conferido nesta solicitação.'}
                        </Text>
                        <HStack mt={2} spacing={2} wrap="wrap">
                            <Button size={botaoMapa} colorScheme="orange" onClick={() => alterar({ pontoConfirmado: true })}>
                                O pino está no lugar certo
                            </Button>
                            <Text fontSize="xs" color="gray.600">ou arraste-o até a porta</Text>
                        </HStack>
                    </Box>
                </Alert>
            )}

        </VStack>
    );

    const usarEnderecoDoMapa = () => {
        // Troca a rua e o que vem com ela. O numero nao: o reverso devolve o
        // imovel mais proximo que conhece, nao a casa do cliente.
        alterar((atual) => aplicarGeocode(
            atual,
            divergente.componentes || {},
            { numero: atual.numero, divergenciaAssumida: false, pontoConfirmado: true, enderecoConfirmado: false },
            'substituir',
        ));
        setDivergente(null);
    };

    const manterOEndereco = () => {
        // O servidor confere isto tambem: sem a marca ele barra a corrida, com
        // ela registra o aviso e deixa passar.
        alterar({ divergenciaAssumida: true });
        setDivergente(null);
    };

    /**
     * Confirmacao de troca de endereco ao mover o pino.
     *
     * Mover o pino nunca reescreve o endereco sozinho — foi assim que o numero
     * da casa sumia. Mas ficar so avisando tambem nao servia: o operador move o
     * pino justamente porque o endereco esta errado. Entao o mapa mostra o que
     * viu naquele ponto, campo a campo, e quem troca e quem clica.
     *
     * Nao e um <Modal> do Chakra de proposito. Modal dentro de modal quebra: o
     * de fora usa react-remove-scroll, que poe pointer-events: none em tudo
     * que estiver fora dele, e o de dentro vive em outro portal — a tela
     * inteira ficava cinza e nada clicava, nem os botoes deste dialogo.
     * Aqui e um Portal simples com sobreposicao propria: sem focus lock, sem
     * remove-scroll, sem briga de empilhamento.
     */
    const dialogoDivergencia = divergente ? (
        <Portal>
            <Flex
                position="fixed"
                top="var(--taga-mapa-y, 0px)"
                left="var(--taga-mapa-x, 0px)"
                w="var(--taga-mapa-w, 100vw)"
                h="var(--taga-mapa-h, 100dvh)"
                maxW="100vw"
                zIndex={2100}
                bg="blackAlpha.600"
                align="center"
                justify="center"
                px={4}
                onClick={manterOEndereco}
            >
                <Box
                    bg="white"
                    borderRadius="lg"
                    boxShadow="xl"
                    maxW="420px"
                    w="100%"
                    maxH="calc(var(--taga-mapa-h, 100dvh) - 32px)"
                    overflowY="auto"
                    role="dialog"
                    aria-modal="true"
                    aria-label="O mapa encontrou outro endereço"
                    onClick={(e) => e.stopPropagation()}
                >
                    <HStack spacing={2} px={4} pt={4} pb={2}>
                        <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={4} />
                        <Text fontSize="md" fontWeight="bold">O mapa encontrou outro endereço</Text>
                    </HStack>

                    <Box px={4} pb={2}>
                        <Text fontSize="xs" color="gray.600" mb={2}>Neste ponto do mapa fica:</Text>
                        <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3} mb={3}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.800">{divergente.linha}</Text>
                        </Box>

                        <VStack align="stretch" spacing={2}>
                            {(divergente.campos || []).map((campo) => (
                                <Flex
                                    key={campo}
                                    justify="space-between"
                                    gap={compacto ? 0 : 3}
                                    fontSize="xs"
                                    align={compacto ? 'stretch' : 'start'}
                                    direction={compacto ? 'column' : 'row'}
                                >
                                    <Text color="gray.500" flexShrink={0}>{ROTULO_CAMPO[campo] || campo}</Text>
                                    <Flex
                                        gap={2}
                                        wrap="wrap"
                                        justify={compacto ? 'flex-start' : 'flex-end'}
                                        flex={1}
                                        minW={0}
                                    >
                                        <Text color="gray.400" textDecoration="line-through" noOfLines={compacto ? undefined : 1}>
                                            {campo === 'cep' ? formatarCep(destino[campo]) : (destino[campo] || '-')}
                                        </Text>
                                        <Text color="orange.600" fontWeight="bold" noOfLines={compacto ? undefined : 1}>
                                            {campo === 'cep'
                                                ? formatarCep(divergente.componentes?.[campo])
                                                : (divergente.componentes?.[campo] || '-')}
                                        </Text>
                                    </Flex>
                                </Flex>
                            ))}
                        </VStack>

                        {divergente.numeroPreservado && (
                            <Text fontSize="xs" color="gray.600" mt={3}>
                                Número <b>{divergente.numeroPreservado}</b> não muda. Confira na rua nova.
                            </Text>
                        )}
                    </Box>

                    <Flex
                        px={4}
                        py={3}
                        gap={2}
                        borderTopWidth="1px"
                        borderColor="gray.100"
                        direction={compacto ? 'column-reverse' : 'row'}
                        justify="flex-end"
                    >
                        <Button size={tamanho} variant="ghost" onClick={manterOEndereco}>
                            Manter o que eu escrevi
                        </Button>
                        <Button size={tamanho} colorScheme="orange" onClick={usarEnderecoDoMapa} autoFocus>
                            Usar o endereço do mapa
                        </Button>
                    </Flex>
                </Box>
            </Flex>
        </Portal>
    ) : null;

    /**
     * O mapa ocupando a tela toda, fora do modal.
     *
     * Vive num Portal de proposito: dentro do ModalContent o `position: fixed`
     * se ancoraria no modal, porque o Chakra deixa um transform parado ali.
     * O no do mapa e transplantado para ca e devolvido ao fechar.
     */
    const telaCheia = mapaCheio ? (
        <Portal>
            <Flex
                position="fixed"
                top="var(--taga-mapa-y, 0px)"
                left="var(--taga-mapa-x, 0px)"
                w="var(--taga-mapa-w, 100vw)"
                h="var(--taga-mapa-h, 100dvh)"
                maxW="100vw"
                overflow="hidden"
                zIndex={2000}
                bg="white"
                direction="column"
                role="dialog"
                aria-label="Mapa em tela cheia"
            >
                {/* No celular o botao vai embaixo e ocupa a largura toda: ao
                    lado do endereco ele era a primeira coisa a ser empurrada
                    para fora da tela. */}
                <Flex
                    px={3}
                    py={2}
                    gap={2}
                    align={compacto ? 'stretch' : 'center'}
                    direction={compacto ? 'column' : 'row'}
                    justify="space-between"
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                    flexShrink={0}
                >
                    <Box minW={0} flex={1}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
                            Ponto de entrega
                        </Text>
                        <Text fontSize="sm" color="gray.800" noOfLines={1}>{linha || 'sem endereço ainda'}</Text>
                    </Box>
                    <Button
                        size="sm"
                        colorScheme="purple"
                        leftIcon={<Icon as={FaCheck} boxSize={3} />}
                        onClick={() => setMapaCheio(false)}
                        flexShrink={0}
                        w={compacto ? '100%' : undefined}
                    >
                        Pronto
                    </Button>
                </Flex>

                <Box ref={refCaixaCheia} flex={1} minH={0} bg="gray.100" position="relative">
                    {/* Ancorados a esquerda: a direita e o lado que some
                        quando o container fica maior que a tela. O +/- do
                        Leaflet esta no canto de baixo, entao aqui em cima e
                        espaco livre. */}
                    <HStack position="absolute" top={2} left={2} zIndex={1000} spacing={1}>
                        {pontoAnterior && (
                            <IconButton size={botaoMapa} aria-label="Desfazer" icon={<Icon as={FaUndo} boxSize={3} />}
                                onClick={desfazerPino} bg="white" boxShadow="md" />
                        )}
                        {temPonto(destino) && (
                            <IconButton size={botaoMapa} aria-label="Centralizar no pino" icon={<Icon as={FaCrosshairs} boxSize={3} />}
                                onClick={() => posicionar(destino.lat, destino.lng, 18)} bg="white" boxShadow="md" />
                        )}
                        <IconButton size={botaoMapa} aria-label="fechar" icon={<Icon as={FaCompress} boxSize={3} />}
                            onClick={() => setMapaCheio(false)} bg="white" boxShadow="md" />
                    </HStack>
                </Box>

                <Flex px={3} py={2} gap={2} align="center" justify="space-between" borderTopWidth="1px" borderColor="gray.200" flexShrink={0} wrap="wrap">
                    <HStack spacing={2} minW={0}>
                        {resolvendoPino && <Spinner size="xs" color="purple.500" />}
                        {info && <Badge colorScheme={info.cor} variant="subtle" fontSize="10px">{info.rotulo}</Badge>}
                    </HStack>
                    <Text fontSize="xs" color="gray.500">Arraste o pino até a porta.</Text>
                </Flex>
            </Flex>
        </Portal>
    ) : null;

    return (
        <VStack align="stretch" spacing={3}>
            {telaCheia}
            {dialogoDivergencia}
            <Box position="relative">
                <InputGroup size={tamanho}>
                    <InputLeftElement pointerEvents="none" h="100%">
                        <Icon as={FaSearch} color="gray.400" boxSize={3} />
                    </InputLeftElement>
                    <Input
                        value={termo}
                        onChange={(e) => aoDigitar(e.target.value)}
                        onKeyDown={aoTeclar}
                        onFocus={() => sugestoes.length > 0 && setMostrarLista(true)}
                        onBlur={() => setTimeout(() => setMostrarLista(false), 180)}
                        placeholder="Buscar endereço ou CEP para marcar no mapa"
                        aria-label="Buscar endereço no mapa"
                        role="combobox"
                        aria-expanded={mostrarLista && sugestoes.length > 0}
                        aria-controls="sugestoes-endereco"
                        aria-activedescendant={destacado >= 0 ? `sugestao-${destacado}` : undefined}
                        aria-busy={buscando}
                        bg="white"
                        borderRadius="lg"
                        pr={10}
                        autoComplete="off"
                    />
                    <InputRightElement h="100%" w={10}>
                        {buscando ? (
                            <Spinner size="xs" color="purple.500" aria-label="buscando" />
                        ) : termo ? (
                            <Icon as={FaTimes} color="gray.400" boxSize={3} cursor="pointer" onClick={() => { setTermo(''); setSugestoes([]); setBuscouSemResultado(false); }} />
                        ) : null}
                    </InputRightElement>
                </InputGroup>

                {mostrarLista && sugestoes.length > 0 && (
                    <VStack
                        align="stretch" spacing={0} position="absolute" top="calc(100% + 4px)" left={0} right={0}
                        zIndex={1500} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg"
                        boxShadow="lg" maxH="240px" overflowY="auto" role="listbox" id="sugestoes-endereco"
                    >
                        {sugestoes.map((s, i) => (
                            <Box
                                key={`${s.lat}-${s.lng}-${i}`}
                                id={`sugestao-${i}`}
                                px={3} py={compacto ? 3 : 2} cursor="pointer" role="option" aria-selected={i === destacado}
                                bg={i === destacado ? 'purple.50' : 'white'} _hover={{ bg: 'purple.50' }}
                                borderBottomWidth={i < sugestoes.length - 1 ? '1px' : 0} borderColor="gray.100"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => escolher(s)}
                            >
                                <HStack spacing={2} align="start">
                                    <Icon as={FaMapMarkerAlt} color="purple.400" boxSize={3} mt="3px" flexShrink={0} />
                                    <Box flex={1} minW={0}>
                                        <Text fontSize="sm" color="gray.800" noOfLines={2}>{s.rotulo}</Text>
                                        <HStack spacing={2} mt="2px">
                                            {PRECISAO[s.precisao] && (
                                                <Badge colorScheme={PRECISAO[s.precisao].cor} variant="subtle" fontSize="9px">
                                                    {PRECISAO[s.precisao].rotulo}
                                                </Badge>
                                            )}
                                            {s.distancia_km !== undefined && (
                                                <Text fontSize="10px" color="gray.500">{s.distancia_km} km da loja</Text>
                                            )}
                                        </HStack>
                                    </Box>
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                )}
            </Box>

            {buscouSemResultado && (
                <Alert status="info" borderRadius="md" fontSize="xs">
                    <AlertIcon boxSize={3} />
                    Não achei esse endereço no mapa. Confira os campos abaixo e toque no mapa para marcar o ponto.
                </Alert>
            )}

            {falhaGeocode && (
                <Alert status="warning" borderRadius="md" fontSize="xs" alignItems="start">
                    <AlertIcon boxSize={3} mt="2px" />
                    <Box flex={1}>
                        <Text>{falhaGeocode}</Text>
                        <Button mt={1} size={botaoMapa} variant="outline" onClick={localizarEscrito} isLoading={buscando}>
                            tentar de novo
                        </Button>
                    </Box>
                </Alert>
            )}

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
                <GridItem>{formulario}</GridItem>
                <GridItem>{mapa}</GridItem>
            </Grid>

            <Flex gap={2} wrap="wrap" justify="space-between" align="center">
                <Button size={botaoMapa} variant="ghost" leftIcon={<Icon as={FaMapMarkerAlt} boxSize={3} />} onClick={localizarEscrito} isLoading={buscando}>
                    localizar no mapa o que está escrito
                </Button>
                {cadastro && (
                    <Button size={botaoMapa} variant="ghost" colorScheme="gray" leftIcon={<Icon as={FaUndo} boxSize={3} />} onClick={restaurarDaVenda}>
                        restaurar endereço da venda
                    </Button>
                )}
            </Flex>

            <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>
                    O motoboy vai receber assim
                </Text>
                <Text fontSize="sm" color="gray.800" fontWeight="medium">{linha || '-'}</Text>
                {observacaoDoMotorista(destino) && (
                    <Text fontSize="xs" color="gray.600" mt={1}>{observacaoDoMotorista(destino)}</Text>
                )}
            </Box>
        </VStack>
    );
};

export default SeletorEnderecoMapa;
